import { clearActivationContinuation, readPendingOnboarding, savePendingOnboarding } from 'services/auth/onboardingStatus';

const ACTIVE_WORKSPACE_KEY = 'workplansiq.activeWorkspace';
const LEGACY_SELECTED_TENANT_KEY = 'selectedTenant';
const DEMO_MEMBERSHIPS_KEY = 'workplansiq.demoOrganizations';

const migrateLegacyWorkspace = (storage) => {
  const current = storage.getItem(ACTIVE_WORKSPACE_KEY);
  if (current) return current;

  const legacy = storage.getItem(LEGACY_SELECTED_TENANT_KEY);
  if (!legacy) return null;

  storage.setItem(ACTIVE_WORKSPACE_KEY, legacy);
  storage.removeItem(LEGACY_SELECTED_TENANT_KEY);
  return legacy;
};

// Run the storage-key migration as soon as this module is loaded, including on
// the login page before a workspace is selected.
if (typeof window !== 'undefined') {
  migrateLegacyWorkspace(window.sessionStorage);
  migrateLegacyWorkspace(window.localStorage);
}

const MOCK_TENANT_MEMBERSHIPS = [
  {
    id: 'cerdas-inovasi',
    name: 'PT Cerdas Inovasi',
    identifier: 'cerdasinovasi.worksplaniq.com',
    plan: 'Professional',
    role: 'Organization Administrator',
    roleId: '0f418ead-0b68-4af4-b141-0b894503f559',
    status: 'active',
    orgId: 'mock-org-cerdas-inovasi',
    wcId: 'mock-wc-cerdas-inovasi',
  },
  {
    id: 'maju-bersama',
    name: 'PT Maju Bersama',
    identifier: 'majubersama.worksplaniq.com',
    plan: 'Enterprise',
    role: 'Reviewer',
    roleId: '5febd304-7990-4ddd-a9ce-18812aae2a4f',
    status: 'active',
    orgId: 'mock-org-maju-bersama',
    wcId: 'mock-wc-maju-bersama',
  },
  {
    id: 'solusi-prima',
    name: 'PT Solusi Prima',
    identifier: 'solusiprima.worksplaniq.com',
    plan: 'Starter',
    role: 'Submitter',
    roleId: '4b03b020-194f-45a9-810a-06801ffa513c',
    status: 'active',
    orgId: 'mock-org-solusi-prima',
    wcId: 'mock-wc-solusi-prima',
  },
  {
    id: 'mandiri-jaya',
    name: 'PT Mandiri Jaya',
    identifier: 'mandirijaya.worksplaniq.com',
    plan: 'Starter',
    role: 'Approver',
    roleId: '801e1d35-a9b5-4ef3-972d-c005cf503243',
    status: 'pending',
    orgId: 'mock-org-mandiri-jaya',
    wcId: 'mock-wc-mandiri-jaya',
  },
  {
    id: 'inovasi-digital',
    name: 'PT Inovasi Digital',
    identifier: 'inovasidigital.worksplaniq.com',
    plan: 'Professional',
    role: 'Reviewer',
    roleId: '5febd304-7990-4ddd-a9ce-18812aae2a4f',
    status: 'suspended',
    orgId: 'mock-org-inovasi-digital',
    wcId: 'mock-wc-inovasi-digital',
  },
];

export const getTenantMemberships = async () => {
  try {
    const demoMemberships = JSON.parse(sessionStorage.getItem(DEMO_MEMBERSHIPS_KEY));
    if (Array.isArray(demoMemberships) && demoMemberships.length) {
      return Promise.resolve(demoMemberships);
    }
  } catch { /* Continue with memberships from the normal source. */ }

  const onboardingTenant = getSelectedTenant();

  if (onboardingTenant?.source === 'onboarding-mock') {
    const onboarding = readPendingOnboarding();
    const signedInEmail = sessionStorage.getItem('workplansiq.signedInEmail')?.trim().toLowerCase();
    const workspaceEmail = onboardingTenant.ownerEmail?.trim().toLowerCase()
      || onboarding?.email?.trim().toLowerCase();
    const belongsToSignedInUser = Boolean(signedInEmail && workspaceEmail === signedInEmail);
    const workspaceIsReady = onboarding?.onboardingStatus === 'WORKSPACE_READY';

    if (belongsToSignedInUser && workspaceIsReady) {
      return Promise.resolve([onboardingTenant]);
    }
  }

  // Production must not grant mock workspace memberships to every account.
  // Enable this explicitly only when reviewing the tenant-selection UI locally.
  const mockEnabled =
    process.env.REACT_APP_USE_MOCK_TENANT_MEMBERSHIPS === 'true';

  return Promise.resolve(mockEnabled ? MOCK_TENANT_MEMBERSHIPS : []);
};

export const saveDemoTenantMemberships = (memberships) => {
  sessionStorage.setItem(DEMO_MEMBERSHIPS_KEY, JSON.stringify(memberships));
};

export const getSelectedTenant = () => {
  try {
    const sessionTenant = JSON.parse(migrateLegacyWorkspace(sessionStorage)) || null;
    if (sessionTenant) {
      localStorage.removeItem(LEGACY_SELECTED_TENANT_KEY);
      if (sessionTenant.source === 'onboarding-mock') {
        localStorage.setItem(ACTIVE_WORKSPACE_KEY, JSON.stringify(sessionTenant));
      }
      return sessionTenant;
    }

    const persistedTenant = JSON.parse(migrateLegacyWorkspace(localStorage)) || null;
    if (persistedTenant?.source === 'onboarding-mock') {
      sessionStorage.setItem(ACTIVE_WORKSPACE_KEY, JSON.stringify(persistedTenant));
      sessionStorage.setItem('orgId', persistedTenant.orgId);
      sessionStorage.setItem('wcId', persistedTenant.wcId);
      return persistedTenant;
    }

    return null;
  } catch {
    return null;
  }
};

export const saveSelectedTenant = (tenant) => {
  sessionStorage.setItem(ACTIVE_WORKSPACE_KEY, JSON.stringify(tenant));
  sessionStorage.removeItem(LEGACY_SELECTED_TENANT_KEY);
  localStorage.removeItem(LEGACY_SELECTED_TENANT_KEY);
  if (tenant?.source === 'onboarding-mock') {
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, JSON.stringify(tenant));
    localStorage.removeItem(LEGACY_SELECTED_TENANT_KEY);
  }
  sessionStorage.setItem('orgId', tenant.orgId);
  sessionStorage.setItem('wcId', tenant.wcId);
  try {
    const demoMemberships = JSON.parse(sessionStorage.getItem(DEMO_MEMBERSHIPS_KEY));
    if (Array.isArray(demoMemberships) && demoMemberships.some((item) => item.id === tenant.id)) {
      sessionStorage.setItem(DEMO_MEMBERSHIPS_KEY, JSON.stringify(
        demoMemberships.map((item) => item.id === tenant.id ? tenant : item),
      ));
    }
  } catch { /* Demo membership synchronization is best effort only. */ }
};

export const provisionTemporaryWorkspace = ({ ownerEmail, selectedPlan } = {}) => {
  const draft = readPendingOnboarding() || {};
  const plan = selectedPlan || {};
  const timestamp = Date.now();
  const identifier = `workspace-${timestamp}`;
  const hasSelectedPlan = Boolean(plan.title || plan.code || plan.id || draft.planTitle || draft.planCode);
  const invoiceIssued = Boolean(plan.invoiceId || draft.invoiceId || draft.invoiceStatus === 'PENDING');
  const temporaryAccessEndAt = invoiceIssued
    ? new Date(timestamp + (14 * 24 * 60 * 60 * 1000)).toISOString()
    : null;
  const tenant = {
    id: identifier,
    name: 'New WorkPlansIQ Organization',
    identifier,
    plan: plan.title || draft.planTitle || draft.planCode || 'Plan not selected',
    role: 'Organization Administrator',
    roleId: '0f418ead-0b68-4af4-b141-0b894503f559',
    status: invoiceIssued ? 'temporary-access' : 'active',
    subscriptionStatus: invoiceIssued ? 'INVOICE_PENDING' : hasSelectedPlan ? 'PLAN_DRAFT' : 'NO_SUBSCRIPTION',
    paymentStatus: invoiceIssued ? 'PENDING_PAYMENT' : 'NOT_STARTED',
    accessStatus: invoiceIssued ? 'TEMPORARY_ACCESS' : 'EXPLORATION',
    workspaceSetupStatus: 'PENDING',
    workspaceSetupStep: 0,
    workspaceSetupFlowVersion: 2,
    temporaryAccessEndAt,
    paymentDueAt: temporaryAccessEndAt,
    invoiceIssuedAt: invoiceIssued ? new Date(timestamp).toISOString() : null,
    orgId: `mock-org-${timestamp}`,
    wcId: `mock-wc-${timestamp}`,
    source: 'onboarding-mock',
    ownerEmail: String(ownerEmail || draft.email || '').trim().toLowerCase(),
  };

  saveSelectedTenant(tenant);
  savePendingOnboarding({
    ...draft,
    planCode: draft.planCode || plan.code || plan.id,
    planTitle: draft.planTitle || plan.title,
    capacity: draft.capacity || plan.capacity,
    durationMonths: draft.durationMonths || plan.durationMonths,
    onboardingStatus: 'WORKSPACE_READY',
    workspaceCreatedAt: new Date(timestamp).toISOString(),
  });
  clearActivationContinuation();
  return tenant;
};
